<script lang="ts">
  import { onMount } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  
  export let options: Array<{uuid: string, name: string}> = [];
  export let selectedValues: string[] = [];
  export let placeholder: string = 'Select...';
  
  const dispatch = createEventDispatcher();
  
  let isOpen = false;
  let searchInput = '';
  let dropdownRef: HTMLDivElement;
  
  $: filteredOptions = options.filter(option => 
    option.name.toLowerCase().includes(searchInput.toLowerCase())
  );
  
  $: displayText = selectedValues.length === 0 
    ? placeholder 
    : selectedValues.length === 1
    ? options.find(opt => opt.uuid === selectedValues[0])?.name || placeholder
    : `${selectedValues.length} selected`;
  
  function toggleSelection(optionUuid: string) {
    if (selectedValues.includes(optionUuid)) {
      selectedValues = selectedValues.filter(uuid => uuid !== optionUuid);
    } else {
      selectedValues = [...selectedValues, optionUuid];
    }
    dispatch('change', selectedValues);
  }
  
  function clearAll() {
    selectedValues = [];
    dispatch('change', []);
  }
  
  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      isOpen = false;
      searchInput = '';
    }
  }
  
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      isOpen = false;
      searchInput = '';
    }
  }
  
  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    };
  });
</script>

<div class="multi-select-container" bind:this={dropdownRef}>
  <button
    type="button"
    class="multi-select-trigger"
    class:open={isOpen}
    on:click={() => { isOpen = !isOpen; searchInput = ''; }}
  >
    <span class="trigger-text">{displayText}</span>
    <div class="trigger-icons">
      {#if selectedValues.length > 0}
        <button
          type="button"
          class="clear-button"
          on:click|stopPropagation={clearAll}
          title="Clear selection"
        >
          <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      {/if}
      <svg class="dropdown-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
        <path d="M7 10l5 5 5-5z"/>
      </svg>
    </div>
  </button>
  
  {#if isOpen}
    <div class="dropdown">
      <div class="dropdown-header">
        <input
          type="text"
          class="search-input"
          placeholder="Search..."
          bind:value={searchInput}
          on:click|stopPropagation
        />
      </div>
      
      <div class="options-list">
        {#if filteredOptions.length === 0}
          <div class="no-results">No results found</div>
        {:else}
          {#each filteredOptions as option}
            <label class="option-label">
              <input
                type="checkbox"
                checked={selectedValues.includes(option.uuid)}
                on:change={() => toggleSelection(option.uuid)}
                class="checkbox-input"
              />
              <span class="option-text">{option.name}</span>
            </label>
          {/each}
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .multi-select-container {
    position: relative;
    display: inline-block;
    min-width: 200px;
  }
  
  .multi-select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 50rem;
    background: white;
    font-size: 0.875rem;
    color: #374151;
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  
  .multi-select-trigger:hover {
    border-color: #9ca3af;
  }
  
  .multi-select-trigger.open,
  .multi-select-trigger:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }
  
  .trigger-text {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .trigger-icons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: 0.5rem;
    flex-shrink: 0;
  }
  
  .clear-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem;
    border: none;
    background: none;
    color: #6b7280;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.2s, color 0.2s;
  }
  
  .clear-button:hover {
    background-color: #f3f4f6;
    color: #111827;
  }
  
  .dropdown-icon {
    color: #6b7280;
    transition: transform 0.2s;
  }
  
  .multi-select-trigger.open .dropdown-icon {
    transform: rotate(180deg);
  }
  
  .dropdown {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 50;
    max-height: 300px;
    display: flex;
    flex-direction: column;
  }
  
  .dropdown-header {
    padding: 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .search-input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  
  .search-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }
  
  .options-list {
    overflow-y: auto;
    max-height: 250px;
    padding: 0.25rem;
  }
  
  .option-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    border-radius: 6px;
    transition: background-color 0.2s;
  }
  
  .option-label:hover {
    background-color: #f9fafb;
  }
  
  .checkbox-input {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
    accent-color: var(--color-primary);
  }
  
  .option-text {
    font-size: 0.875rem;
    color: #374151;
    user-select: none;
  }
  
  .no-results {
    padding: 1rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
  }
  
  .options-list::-webkit-scrollbar {
    width: 6px;
  }
  
  .options-list::-webkit-scrollbar-track {
    background: #f7fafc;
  }
  
  .options-list::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 3px;
  }
  
  .options-list::-webkit-scrollbar-thumb:hover {
    background: #cbd5e0;
  }
</style>

